/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * The full country list for the phone country-picker UI (every
 * emergency-contact phone input + the Walk Me Home manual-entry fallback).
 * Deliberately NOT a new dependency: the ISO region codes and dial codes
 * come from libphonenumber-js's own metadata (already a dependency), names
 * come from the platform's built-in Intl.DisplayNames, and the flag emoji
 * is computed algorithmically from the 2-letter region code (regional
 * indicator symbols) — no flag-icon library needed.
 *
 * This is deliberately a SEPARATE, bigger list from `COUNTRY_OPTIONS` in
 * src/utils/phone.ts (a curated 9-country shortlist for the phone-auth
 * sign-in selector). The country picker here exists specifically so a
 * number from any country can be entered correctly — limiting it to 9
 * countries would just move the "silently stored under the wrong country"
 * risk this picker exists to close.
 */

import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

export interface PhoneCountry {
  region: CountryCode;
  dialCode: string;
  name: string;
  flag: string;
}

/** 2-letter ISO region code → flag emoji (regional indicator symbols). */
export function flagEmoji(region: string): string {
  return region
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt(0)),
    );
}

const regionNames: { of(region: string): string } | null =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

function regionName(region: CountryCode): string {
  return regionNames?.of(region) ?? region;
}

let cache: readonly PhoneCountry[] | null = null;

/** Every libphonenumber-supported country, sorted by display name. */
export function allPhoneCountries(): readonly PhoneCountry[] {
  if (cache) return cache;
  const built: PhoneCountry[] = [];
  for (const region of getCountries()) {
    try {
      built.push({
        region,
        dialCode: `+${getCountryCallingCode(region)}`,
        name: regionName(region),
        flag: flagEmoji(region),
      });
    } catch {
      // A handful of libphonenumber "countries" are non-geographic entities
      // without a calling code lookup in some metadata builds — skip them.
    }
  }
  built.sort((a, b) => a.name.localeCompare(b.name));
  cache = built;
  return built;
}

export function phoneCountryByRegion(
  region: CountryCode,
): PhoneCountry | null {
  return allPhoneCountries().find((c) => c.region === region) ?? null;
}

/**
 * Flag + pretty international formatting for an already-normalized E.164
 * number, e.g. "+919876543210" → { flag: "🇮🇳", formatted: "+91 98765 43210" }.
 * Used for the "about to save this number" preview so a wrong-country
 * result is visible before the user confirms. Returns null if the number
 * doesn't parse (shouldn't happen for a value normalizePhone already
 * accepted, but this never throws either way).
 */
export function describeE164(
  e164: string,
): { flag: string; formatted: string } | null {
  try {
    const parsed = parsePhoneNumberFromString(e164);
    if (!parsed || !parsed.country) return null;
    return { flag: flagEmoji(parsed.country), formatted: parsed.formatInternational() };
  } catch {
    return null;
  }
}
